import { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, ChevronLeft, ChevronRight, LocateFixed } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusPill } from '@/components/status-pill'
import { useActionsWithoutProject, useProjectsWithActions } from '@/api/hooks'
import { actionTarget } from './action-target'
import { Link } from '@tanstack/react-router'
import { focusTarget } from '@/hooks/use-action-focus'
import { PurchaseOrderDetailPage } from '@/pages/purchase-order-detail'
import { CustomerCommunicationDetailPage } from '@/pages/customer-communication-detail'
import { OrderDetailPage } from '@/pages/order-detail'
import { ProjectDetailPage } from '@/pages/project-detail'
import { ManufacturerDetailPage } from '@/pages/manufacturer-detail'
import { OrderReviewFindingPage } from '@/pages/order-review-finding'
import { InvoiceDetailPage } from '@/features/invoices/detail/page'
import { useManufacturerDataActions } from '@/features/manufacturer-data/hooks'
import { MailboxBlockItem } from '@/features/manufacturer-data/mailbox-block-item'
import { WarrantyActionItem } from '@/features/warranty-claims/claim-actions'
import { formatStatus } from '@/lib/utils'
import {
  EMPTY_QUEUE,
  advance,
  dataQueueEntries,
  entryKey,
  liveEntry,
  queueEntries,
  retreat,
  withoutProjectEntries,
  upcoming,
  type QueueEntry,
  type QueueState,
  type QueueStatus,
} from './queue-model'
import { TaxExemptionDetailPage } from '@/features/tax-exemptions/tax-exemption-detail-page'

type QueueParams = Omit<NonNullable<Parameters<typeof useProjectsWithActions>[0]>, 'page' | 'limit' | 'progress'>
type DataQueueParams = Omit<Parameters<typeof useManufacturerDataActions>[0], 'page' | 'limit' | 'progress'>

// The queue walks the table's first 100 projects; it reloads as items close, so later ones come into reach.
const QUEUE_PROJECT_LIMIT = 100

function storageKey(signature: string) {
  return `usars:action-queue:${signature}`
}

/** The walk survives opening a linked page and coming Back; it lives for the browser tab. */
function restore(signature: string): QueueState {
  try {
    const saved = sessionStorage.getItem(storageKey(signature))
    return saved ? (JSON.parse(saved) as QueueState) : EMPTY_QUEUE
  } catch {
    return EMPTY_QUEUE
  }
}


/**
 * Open action items one at a time, in the table's order, each on the page where it is worked (USA-1469).
 * An item staff close, start or reprioritize stays on screen until they move on, so nothing shifts under them.
 */
export function ActionQueue({
  params,
  dataParams,
  status,
  signature,
}: {
  params: QueueParams
  /** The Data role's filters: its items belong to manufacturers, not projects (USA-1513). */
  dataParams?: DataQueueParams
  status: QueueStatus
  /** The filters the queue was started with; changing them starts a new queue. */
  signature: string
}) {
  // Every status, so the current item's progress shows even after it leaves the filter.
  const projects = useProjectsWithActions({ ...params, page: 1, limit: QUEUE_PROJECT_LIMIT }, !dataParams)
  const manufacturers = useManufacturerDataActions(
    { ...dataParams, page: 1, limit: QUEUE_PROJECT_LIMIT },
    !!dataParams,
  )
  // The table's "No project" group comes first (USA-1184); a project priority filter leaves it out.
  const listsWithoutProject = !dataParams && !params.project_priority && !params.service_tier
  const withoutProject = useActionsWithoutProject(
    {
      search: params.search,
      staff_id: params.staff_id,
      assigned_role: params.assigned_role,
      priority: params.priority,
      action_type: params.action_type,
      limit: QUEUE_PROJECT_LIMIT,
    },
    listsWithoutProject,
  )
  const { data, isLoading } = dataParams ? manufacturers : projects
  const live = useMemo(
    () =>
      dataParams
        ? dataQueueEntries(manufacturers.data?.items ?? [])
        : [
            ...(listsWithoutProject ? withoutProjectEntries(withoutProject.data?.actions ?? []) : []),
            ...queueEntries(projects.data?.items ?? []),
          ],
    [dataParams, listsWithoutProject, manufacturers.data, projects.data, withoutProject.data],
  )
  const [state, setState] = useState<QueueState>(() => restore(signature))

  useEffect(() => {
    sessionStorage.setItem(storageKey(signature), JSON.stringify(state))
  }, [signature, state])

  // The first item opens once the list has loaded, and then stays put while the list refreshes.
  if (data && state.index === -1) setState(advance(state, live, status))

  if (isLoading || state.index === -1) return <QueueSkeleton />

  const remaining = upcoming(state, live, status).length
  const total = state.seen.length + remaining
  const current = state.seen[state.index]

  if (!current) {
    return (
      <QueueEnd
        empty={state.seen.length === 0}
        onStartOver={() => setState(EMPTY_QUEUE)}
        onBack={state.seen.length > 0 ? () => setState(retreat) : undefined}
      />
    )
  }

  return (
    <div className="space-y-4">
      <QueueBar
        entry={current}
        now={liveEntry(current, live)}
        position={state.index + 1}
        total={total}
        onPrevious={state.index > 0 ? () => setState(retreat) : undefined}
        onNext={() => setState((s) => advance(s, live, status))}
      />
      <QueueItemPage key={entryKey(current)} entry={current} />
    </div>
  )
}

function QueueBar({
  entry,
  now,
  position,
  total,
  onPrevious,
  onNext,
}: {
  entry: QueueEntry
  /** The item's live row; undefined once it is resolved or dismissed. */
  now: QueueEntry | undefined
  position: number
  total: number
  onPrevious?: () => void
  onNext: () => void
}) {
  const { action } = entry
  const handled = !now || !!now.action.in_progress
  const reference = action.po_number ?? action.order_number
  return (
    <div
      role="region"
      aria-label="Action item queue"
      className="sticky -top-4 z-30 -mx-1 flex items-center gap-2 rounded-lg border bg-background px-2 py-1.5 shadow-sm md:-top-6 md:gap-3 md:px-3"
    >
      <p className="shrink-0 text-sm font-semibold tabular-nums" aria-live="polite">
        {position}
        <span className="font-normal text-muted-foreground"> of {total}</span>
      </p>
      <span className="h-5 w-px shrink-0 bg-border" aria-hidden />
      <button
        type="button"
        onClick={() => focusTarget(`[data-action-uuid="${CSS.escape(action.uuid)}"]`)}
        title="Show this item"
        className="group flex min-w-0 cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <LocateFixed className="size-3.5 shrink-0 text-muted-foreground group-hover:text-foreground" aria-hidden />
        <span className="truncate font-medium whitespace-nowrap">
          {action.card?.kind_label ?? formatStatus(action.action_type)}
          {reference && <span className="font-mono text-xs font-normal text-muted-foreground"> · {reference}</span>}
        </span>
      </button>
      {/* A mailbox item's kind already names it (USA-1729). */}
      {entry.project.kind === 'mailbox' ? null : entry.project.kind === 'no_project' ? (
        <span className="min-w-0 truncate text-sm text-muted-foreground max-xl:hidden">{entry.project.name}</span>
      ) : entry.project.kind === 'manufacturer' ? (
        <Link
          to="/manufacturers/$manuId"
          params={{ manuId: entry.project.uuid }}
          className="min-w-0 truncate text-sm text-muted-foreground hover:text-foreground hover:underline max-xl:hidden"
        >
          {entry.project.name}
        </Link>
      ) : (
        <Link
          to="/projects/$projectId"
          params={{ projectId: entry.project.uuid }}
          className="min-w-0 truncate text-sm text-muted-foreground hover:text-foreground hover:underline max-xl:hidden"
        >
          {entry.project.name}
        </Link>
      )}
      {/* The card shows In progress itself; a closed card folds away, so the bar says so. */}
      <span className="shrink-0" aria-live="polite">
        {!now && (
          <StatusPill tone="success" className="whitespace-nowrap">
            Closed
          </StatusPill>
        )}
      </span>
      <div className="ml-auto flex shrink-0 items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrevious}
          disabled={!onPrevious}
          aria-label="Previous item"
          className="max-sm:w-8 max-sm:px-0"
        >
          <ChevronLeft className="size-4" aria-hidden />
          <span className="max-sm:hidden">Previous</span>
        </Button>
        <Button
          variant={handled ? 'default' : 'outline'}
          size="sm"
          onClick={onNext}
          aria-label="Next item"
          className="max-sm:w-8 max-sm:px-0"
        >
          <span className="max-sm:hidden">Next</span>
          <ChevronRight className="size-4" aria-hidden />
        </Button>
      </div>
    </div>
  )
}

/** The page the table's row opens, scrolled to the item's card rather than to the email that raised it. */
function QueueItemPage({ entry }: { entry: QueueEntry }) {
  const actionUuid = entry.action.uuid
  if (entry.project.kind === 'mailbox') return <MailboxBlockItem actionId={entry.action.id} />
  // A Data item (USA-1474): an order review finding on its review page (USA-1513), the rest on the Emails tab.
  if (entry.project.kind === 'manufacturer') {
    const findingId = entry.action.card?.order_finding_uuid
    if (findingId) return <OrderReviewFindingPage findingId={findingId} />
    const email = entry.action.card?.source_email
    return (
      <ManufacturerDetailPage
        manuId={entry.project.uuid}
        tab="emails"
        messageId={email?.message_id ?? undefined}
        actionUuid={email ? undefined : actionUuid}
      />
    )
  }
  const target = actionTarget(entry.action, entry.project.uuid)
  switch (target.kind) {
    case 'purchase_order':
      return <PurchaseOrderDetailPage poId={target.poId} actionUuid={actionUuid} />
    case 'customer_thread':
      return <CustomerCommunicationDetailPage threadId={target.threadId} actionUuid={actionUuid} />
    case 'order':
      return <OrderDetailPage orderId={target.orderId} actionUuid={actionUuid} />
    case 'project':
      return <ProjectDetailPage projectId={target.projectId} actionUuid={actionUuid} />
    case 'invoice':
      return <InvoiceDetailPage invoiceId={target.invoiceId} actionUuid={actionUuid} />
    case 'tax_exemption':
      return <TaxExemptionDetailPage formId={target.formId} />
    case 'warranty_item':
      return <WarrantyActionItem actionUuid={target.actionUuid} />
  }
}

function QueueEnd({
  empty,
  onStartOver,
  onBack,
}: {
  empty: boolean
  onStartOver: () => void
  onBack?: () => void
}) {
  if (empty) return <div className="py-12 text-center text-muted-foreground">No action items</div>
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border bg-background py-12 text-center">
      <CheckCircle2 className="size-8 text-green-600" aria-hidden />
      <p className="font-medium">End of the queue</p>
      <div className="flex gap-2">
        {onBack && (
          <Button variant="outline" size="sm" onClick={onBack}>
            <ChevronLeft className="size-4" aria-hidden />
            Previous
          </Button>
        )}
        <Button size="sm" onClick={onStartOver}>
          Start over
        </Button>
      </div>
    </div>
  )
}

function QueueSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-11 w-full rounded-lg" />
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}
